import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Add, EditOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { fetchAllEmployees, fetchServices, getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import { Employee, Service } from '../../../types';
import { resolveAdminBookingUxConfig } from '../../../utils/bookingUxConfig';
import ServiceFormDialog from '../../../components/services/ServiceFormDialog';

/** Site Builder services panel: see what the homepage shows and add or edit services in place. */
const SiteBuilderServicesPanel: React.FC<{ tenantId: string; disabled?: boolean }> = ({ tenantId, disabled }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const servicesQuery = useQuery<Service[]>({
    queryKey: ['services', tenantId], queryFn: () => fetchServices(tenantId), staleTime: 1000 * 60 * 5,
  });
  const employeesQuery = useQuery<Employee[]>({ queryKey: ['allEmployees', tenantId], queryFn: () => fetchAllEmployees(tenantId) });
  const configQuery = useQuery({ queryKey: ['businessConfig', tenantId], queryFn: () => getCurrentBusinessConfig(tenantId) });
  const ux = resolveAdminBookingUxConfig(configQuery.data ?? null);
  const ownerId = configQuery.data?.paymentInfo?.owner?.trim() ?? '';
  const staff = useMemo(() => {
    const all = (employeesQuery.data ?? []).filter((employee) => employee.active !== false);
    return ux.bookingModel === 'resource' && ownerId ? all.filter((employee) => employee.id !== ownerId) : all;
  }, [employeesQuery.data, ux.bookingModel, ownerId]);
  const services = servicesQuery.data ?? [];

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Services with a photo and “Show on homepage” turned on appear in this section.
      </Typography>
      <Stack spacing={1}>
        {services.map((service) => {
          const onHomepage = Boolean(service.imagePath) && service.showOnHomepage !== false;
          return (
            <Stack key={service.id} direction="row" alignItems="center" spacing={1}
              sx={{ p: 1.25, pl: 1.75, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={800} noWrap>{service.title}</Typography>
                <Chip size="small" sx={{ mt: .5, fontWeight: 700 }}
                  color={onHomepage ? 'success' : 'default'} variant={onHomepage ? 'filled' : 'outlined'}
                  label={onHomepage ? 'On homepage' : service.imagePath ? 'Hidden from homepage' : 'Needs a photo'} />
              </Box>
              <Button size="small" startIcon={<EditOutlined />} disabled={disabled} onClick={() => setEditing(service)}>
                Edit
              </Button>
            </Stack>
          );
        })}
        {!servicesQuery.isLoading && services.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No services yet.</Typography>
        ) : null}
      </Stack>
      <Button variant="outlined" startIcon={<Add />} disabled={disabled || configQuery.isLoading || employeesQuery.isLoading}
        onClick={() => setAdding(true)}>
        Add service
      </Button>
      <ServiceFormDialog open={adding || Boolean(editing)} service={editing ?? undefined}
        onClose={() => { setAdding(false); setEditing(null); }}
        tenantId={tenantId} staff={staff} staffLabel={ux.terminology.resourcePlural}
        categories={configQuery.data?.preferences?.serviceCategories ?? []} />
    </Stack>
  );
};

export default SiteBuilderServicesPanel;
